import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const phoneSchema = z
  .string()
  .regex(/^05\d{8}$/, "\u05DE\u05E1\u05E4\u05E8 \u05D8\u05DC\u05E4\u05D5\u05DF \u05DC\u05D0 \u05EA\u05E7\u05D9\u05DF");

interface OtpFormProps {
  readonly onSubmit: (phone: string, otp: string) => Promise<void>;
}

export function OtpForm({ onSubmit }: OtpFormProps): ReactNode {
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [otp, setOtp] = useState<ReadonlyArray<string>>(
    Array.from({ length: 6 }, () => ""),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);

  const otpRefs = useRef<ReadonlyArray<HTMLInputElement | null>>(
    Array.from({ length: 6 }, () => null),
  );

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const handlePhoneSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setPhoneError(null);

      const result = phoneSchema.safeParse(phone);
      if (!result.success) {
        setPhoneError(
          result.error.issues[0]?.message ??
            "\u05DE\u05E1\u05E4\u05E8 \u05DC\u05D0 \u05EA\u05E7\u05D9\u05DF",
        );
        return;
      }

      setIsLoading(true);
      try {
        // In production: call /api/auth/otp/request
        setStep("otp");
        setResendCountdown(60);
      } catch {
        setPhoneError(
          "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DC\u05D9\u05D7\u05EA \u05D4\u05E7\u05D5\u05D3",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [phone],
  );

  const handleOtpChange = useCallback(
    (index: number, value: string) => {
      if (!/^\d*$/.test(value)) return;

      const digit = value.slice(-1);
      const newOtp = otp.map((d, i) => (i === index ? digit : d));
      setOtp(newOtp);
      setError(null);

      // Auto-advance to next input
      if (digit !== "" && index < 5) {
        const nextRef = otpRefs.current[index + 1];
        if (nextRef) {
          nextRef.focus();
        }
      }

      // Auto-submit on last digit
      if (digit !== "" && index === 5) {
        const fullOtp = newOtp.join("");
        if (fullOtp.length === 6) {
          setIsLoading(true);
          void onSubmit(phone, fullOtp)
            .catch(() => {
              setError(
                "\u05E7\u05D5\u05D3 \u05E9\u05D2\u05D5\u05D9, \u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1",
              );
              setOtp(Array.from({ length: 6 }, () => ""));
              const firstRef = otpRefs.current[0];
              if (firstRef) {
                firstRef.focus();
              }
            })
            .finally(() => setIsLoading(false));
        }
      }
    },
    [otp, phone, onSubmit],
  );

  const handleOtpKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && otp[index] === "" && index > 0) {
        const prevRef = otpRefs.current[index - 1];
        if (prevRef) {
          prevRef.focus();
        }
      }
    },
    [otp],
  );

  const handleResend = useCallback(() => {
    setResendCountdown(60);
    setOtp(Array.from({ length: 6 }, () => ""));
    setError(null);
    // In production: call /api/auth/otp/request again
  }, []);

  if (step === "phone") {
    return (
      <form onSubmit={(e) => void handlePhoneSubmit(e)} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm font-medium">
            {"\u05DE\u05E1\u05E4\u05E8 \u05D8\u05DC\u05E4\u05D5\u05DF"}
          </label>
          <Input
            id="phone"
            type="tel"
            inputMode="numeric"
            placeholder="05XXXXXXXX"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setPhoneError(null);
            }}
            dir="ltr"
            className="text-start text-base"
            autoComplete="tel"
          />
          {phoneError !== null && (
            <p className="text-sm text-destructive">{phoneError}</p>
          )}
        </div>
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading
            ? "\u05E9\u05D5\u05DC\u05D7..."
            : "\u05E9\u05DC\u05D7 \u05E7\u05D5\u05D3"}
        </Button>
      </form>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground text-center">
        {"\u05E9\u05DC\u05D7\u05E0\u05D5 \u05E7\u05D5\u05D3 \u05DC-"}
        <span dir="ltr" className="font-mono">
          {phone}
        </span>
      </p>

      <div className="flex justify-center gap-2" dir="ltr">
        {Array.from({ length: 6 }, (_, i) => (
          <Input
            key={i}
            ref={(el) => {
              (otpRefs.current as Array<HTMLInputElement | null>)[i] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={otp[i]}
            onChange={(e) => handleOtpChange(i, e.target.value)}
            onKeyDown={(e) => handleOtpKeyDown(i, e)}
            className="h-12 w-12 text-center text-lg font-mono"
            autoFocus={i === 0}
            autoComplete="one-time-code"
            disabled={isLoading}
          />
        ))}
      </div>

      {error !== null && (
        <p className="text-sm text-destructive text-center">{error}</p>
      )}

      {isLoading && (
        <p className="text-sm text-muted-foreground text-center">
          {"\u05D1\u05D5\u05D3\u05E7..."}
        </p>
      )}

      <div className="text-center">
        {resendCountdown > 0 ? (
          <p className="text-sm text-muted-foreground">
            {"\u05E9\u05DC\u05D9\u05D7\u05D4 \u05DE\u05D7\u05D3\u05E9 \u05D1\u05E2\u05D5\u05D3 "}
            <span className="font-mono">{resendCountdown}</span>
            {" \u05E9\u05E0\u05D9\u05D5\u05EA"}
          </p>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            className="text-sm text-primary hover:underline cursor-pointer min-h-[44px]"
          >
            {"\u05DC\u05D0 \u05E7\u05D9\u05D1\u05DC\u05EA\u05D9 \u05E7\u05D5\u05D3"}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          setStep("phone");
          setOtp(Array.from({ length: 6 }, () => ""));
          setError(null);
        }}
        className="w-full text-sm text-muted-foreground hover:text-foreground cursor-pointer min-h-[44px]"
      >
        {"\u05E9\u05D9\u05E0\u05D5\u05D9 \u05DE\u05E1\u05E4\u05E8"}
      </button>
    </div>
  );
}
